import { AuditTrailEntryDto } from '@common/audit-trail/auditTrailTypes';
import { t } from 'utils/getTranslations';
import { ActionPill, AuthorAvatar, AuthorName, EntityTypeBadge, RowIdButton } from './AuditTrailBadges';
import { AuditTrailFieldRow } from './AuditTrailFieldRow';
import { dateBucketLabel, formatAuditName, parseChangedAt, timeLabel } from '@common/audit-trail/auditTrailFormat';
import { auditTrailDayLabels } from './auditTrailLabels';
import { ValuePreview } from './ValuePreviewDialog';

interface EntityGroup {
    key: string;
    entry: AuditTrailEntryDto;
}

/** One section per audit row (= one entity mutation), in server (chronological) order. */
function entityGroups(entries: AuditTrailEntryDto[]): EntityGroup[] {
    return entries.map((entry) => ({ key: `${entry.tableName}::${entry.rowId}::${entry.id}`, entry }));
}

function metaLine(entries: AuditTrailEntryDto[]): string {
    const changes = entries.reduce((count, entry) => count + entry.changedProperties.length, 0);
    const records = new Set(entries.map((entry) => `${entry.tableName}::${entry.rowId}`)).size;
    const changesLabel =
        changes === 1 ? t('audit-trail.one-field-change') : t('audit-trail.field-changes', { count: String(changes) });
    const recordsLabel =
        records === 1 ? t('audit-trail.one-record') : t('audit-trail.records', { count: String(records) });
    return `${changesLabel} · ${recordsLabel}`;
}

export function AuditTrailTransactionCard({
    entries,
    onRowIdClick,
    onAuthorClick,
    onPreview,
}: {
    entries: AuditTrailEntryDto[];
    onRowIdClick: (rowId: string) => void;
    onAuthorClick: (authorId: number) => void;
    onPreview: (preview: ValuePreview) => void;
}) {
    const first = entries[0];
    if (!first) {
        return null;
    }
    const changedAt = parseChangedAt(first.changedAt);

    return (
        <div className="bg-paper shadow-card overflow-hidden rounded-xl">
            <div className="border-grey-200 flex items-center gap-3 border-b px-5 py-4">
                <AuthorAvatar entry={first} size="lg" />
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-fg text-[15px] font-semibold">
                            <AuthorName entry={first} onClick={onAuthorClick} />
                        </span>
                        <ActionPill
                            operationType={first.operationType}
                            className="new:text-caption px-[9px] py-0.5 text-[11px]"
                        />
                    </div>
                    <div className="new:text-muted-foreground mt-[3px] text-xs text-[#9A9A9A]">{metaLine(entries)}</div>
                </div>
                <div className="flex-none text-right">
                    <div className="text-grey-600 text-[12.5px] font-medium">
                        {dateBucketLabel(changedAt, auditTrailDayLabels())} · {timeLabel(changedAt)}
                    </div>
                    <div className="text-grey-400 new:text-muted-foreground new:text-caption mt-0.5 font-mono text-[11px]">
                        {first.transactionId}
                    </div>
                </div>
            </div>
            <div className="px-5 pt-1 pb-[18px]">
                {entityGroups(entries).map(({ key, entry }) => {
                    const entityTypeLabel = formatAuditName(entry.tableName);
                    return (
                        <div key={key} className="pt-4">
                            <div className="mb-1 flex items-center gap-2">
                                <EntityTypeBadge label={entityTypeLabel} />
                                <RowIdButton
                                    rowId={entry.rowId}
                                    label={entry.rowLabel}
                                    className="text-xs"
                                    onClick={onRowIdClick}
                                />
                            </div>
                            {entry.changedProperties.length === 0 && (
                                <div className="border-grey-100 text-grey-400 new:text-muted-foreground border-t py-[9px] text-[13px]">
                                    {t('audit-trail.no-changed-properties')}
                                </div>
                            )}
                            {entry.changedProperties.map((property) => (
                                <AuditTrailFieldRow
                                    key={property}
                                    property={property}
                                    oldValue={entry.oldValues[property]}
                                    newValue={entry.newValues[property]}
                                    oldLabel={entry.oldValueLabels?.[property]}
                                    newLabel={entry.newValueLabels?.[property]}
                                    entityTypeLabel={entityTypeLabel}
                                    onPreview={onPreview}
                                />
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
